<template>
  <div>
    <div class="chart-filters-button d-flex justify-content-center align-items-center"
         :class="{'show': chart.showFilters}" @click="toggleFilters()">
      <font-awesome-icon :icon="chart.showFilters ? 'chevron-right' : 'chevron-left'"></font-awesome-icon>
    </div>
    <div class="chart-filters-tab py-3 d-flex flex-column align-items-center justify-content-between"
         :class="{'show': chart.showFilters}">
      <div>
        <div class="input-wrapper mb-4">
          <label>Count:</label>
          <el-select
              placeholder="Select values" collapse-tags
              v-model="chart.filters.count"
          >
            <el-option
                v-for="value in countFilters"
                :key="value.value" :label="value.name" :value="value.value">
            </el-option>
          </el-select>
        </div>
        <div class="chart-filters-title mb-3">Data breakdown:</div>
        <div class="input-wrapper">
          <label>Metadata Type:</label>
          <el-select
              placeholder="Select values" collapse-tags
              v-model="chart.filters.metadataType"
          >
            <el-option
                v-for="value in metadataTypes"
                :key="value.category" :label="value.label" :value="value.category">
            </el-option>
          </el-select>
        </div>
        <div class="input-wrapper mt-2">
          <label>Metadata:</label>
          <el-select
              placeholder="Select values" collapse-tags
              v-model="chart.filters.metadata"
              :disabled="!chart.filters.metadataType"
          >
            <el-option
                v-for="value in metadataValues"
                :key="value.field" :label="value.label" :value="value.field">
            </el-option>
          </el-select>
        </div>
        <button class="search-button btn btn-block mt-3" type="button" :disabled="!areAllFieldsSelected"
                @click="applyFilters">
          Run
        </button>
      </div>
      <img src="../../../assets/images/icons/excel-export-H.svg" class="dwn-button" @click="download('tendencies')"/>
    </div>
    <div class="chart-filters-overlay" :class="{'show': chart.showFilters, hide: chart.hideOverlay}"
         @click="toggleFilters()"></div>
    <div v-if="rawData && Object.keys(rawData).length">
      <BarChart :chart-data="chartData" :options="chart.options" style="height: 300px; margin-bottom: 10px" :data-types="{'Count': 'value'}"></BarChart>
      <div class="table-container">
        <div class="d-flex table-header">
          <div class="column-header" style="width: 33.5%;" @click="changeSort('name')">
            <span class="mr-1" style="text-transform: capitalize">{{currentTag}}</span>
            <div>
              <font-awesome-icon :icon="'caret-up'"
                                 :color="sortBy.key === 'name' && sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
              <font-awesome-icon :icon="'caret-down'"
                                 :color="sortBy.key === 'name' && !sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
            </div>
          </div>
          <div class="column-header" style="width: 33%;" @click="changeSort('count')">
            <span class="mr-1">Number of fragrances</span>
            <div>
              <font-awesome-icon :icon="'caret-up'"
                                 :color="sortBy.key === 'count' && sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
              <font-awesome-icon :icon="'caret-down'"
                                 :color="sortBy.key === 'count' && !sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
            </div>
          </div>
          <div class="column-header" style="width: 33.5%;" @click="changeSort('percentage')">
            <span class="mr-1">Percentage of fragrances</span>
            <div>
              <font-awesome-icon :icon="'caret-up'"
                                 :color="sortBy.key === 'percentage' && sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
              <font-awesome-icon :icon="'caret-down'"
                                 :color="sortBy.key === 'percentage' && !sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
            </div>
          </div>
        </div>
        <div style="height: calc(100% - 50px)">
          <DynamicScroller
              :items="filteredData"
              page-mode
              :min-item-size="50"
              class="scroller"
              :emit-update="true"
          >
            <template v-slot="{ item, index, active }">
              <DynamicScrollerItem
                  :item="item"
                  :active="active"
                  :data-index="index">
                <div class="data d-flex flex-column justify-content-center align-items-center">
                  <div class="main-data" style="padding: 0 10px;">
                    <div class="column-body" style="width: 33%;">
                      {{ formatValue(item.name) }}
                    </div>
                    <div class="column-body" style="width: 33%;">
                      {{ item.count }}
                    </div>
                    <div class="column-body" style="width: 33%;">
                      {{ formatValue(item.percentage) }}
                    </div>
                  </div>
                </div>
              </DynamicScrollerItem>
            </template>
          </DynamicScroller>
        </div>
      </div>
    </div>
    <div v-else-if="localReady.tendencies" class="no-data d-flex justify-content-center align-items-center">
      <i v-if="searchExecuted">No data available for provided filters.</i>
      <i v-else>Select filters in order to see performance data.</i>
    </div>
  </div>
</template>

<script>
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import BarChart from '../../shared/BarChart'
import { mapActions, mapGetters } from 'vuex'
import studyGetters from '../../../store/modules/studies/getterTypes'
import studiesActions from '../../../store/modules/studies/actionTypes'
import fileNames from '../../../constants/fileNames'
import _ from 'lodash'

export default {
  name: 'TendenciesTable',
  emits: ['update:ready'],
  components: { BarChart },
  props: {
    data: {
      type: Array,
      required: false
    },
    fragrances: {
      type: Array,
      default: null
    },
    ready: Object,
    filterFields: Object,
    searchData: Function,
    parseSearchData: Function
  },
  data () {
    return {
      localReady: { ...this.ready },
      searchExecuted: false,
      resizeToggle: false,
      currentTag: '',
      sortBy: {
        key: 'name',
        desc: true
      },
      chart: {
        options: {
          scales: {
            xAxes: [{
              gridLines: {
                display: false
              }
            }],
            yAxes: [{
              ticks: {
                min: 0
              }
            }]
          },
          barValueSpacing: 20,
          responsive: true,
          maintainAspectRatio: false
        },
        showFilters: true,
        hideOverlay: false,
        filters: {
          count: '',
          metadata: '',
          metadataType: ''
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      filters: studyGetters.GET_SEARCH_FILTERS,
      rawData: studyGetters.GET_ANALYTICS_TENDENCIES
    }),
    filteredData () {
      const arrayCopy = this.formatData()
      const sortedArray = arrayCopy.sort((a, b) => {
        if (a[this.sortBy.key] < b[this.sortBy.key]) {
          return -1
        }
        if (a[this.sortBy.key] > b[this.sortBy.key]) {
          return 1
        }
        return 0
      })
      return this.sortBy.desc ? sortedArray : sortedArray.reverse()
    },
    chartData () {
      return Object.keys(this.rawData).length ? {
        labels: this.rawData.chart_data.labels,
        datasets: [{
          label: 'Count',
          backgroundColor: '#3e95cd',
          data: this.rawData.chart_data.data
        }]
      } : {}
    },
    countFilters () {
      return Object.keys(this.filterFields) ? this.filterFields.tendencies_metadata.count : []
    },
    metadataTypes () {
      return Object.keys(this.filterFields) ? this.filterFields.tendencies_metadata.data_breakdown : []
    },
    metadataValues () {
      return Object.keys(this.filterFields) && this.chart.filters.metadataType ? this.filterFields.tendencies_metadata.data_breakdown.find(el => el.category === this.chart.filters.metadataType).options : []
    },
    areAllFieldsSelected () {
      return Object.values(this.chart.filters).every(x => x)
    }
  },
  methods: {
    ...mapActions({
      getXLSX: studiesActions.FETCH_TENDENCIES_XLSX
    }),
    formatData () {
      const formattedArray = []
      this.rawData.table_data.forEach((el, idx) => {
        formattedArray.push({
          id: idx,
          ...el
        })
      })
      return formattedArray
    },
    formatValue (value) {
      if (typeof value === 'string') {
        return value && value.charAt(0).toUpperCase() + value.slice(1)
      } else {
        return value.toFixed(2) + '%'
      }
    },
    toggleFilters () {
      if (!this.chart.hideOverlay) {
        setTimeout(() => this.chart.hideOverlay = true, 200)
      } else {
        this.chart.hideOverlay = false
      }
      setTimeout(() => this.chart.showFilters = !this.chart.showFilters, 0)
    },
    applyFilters () {
      this.localReady.tendencies = false
      this.currentTag = this.chart.filters.metadata
      this.searchData(this.parseSearchData(this.filters), {
        return: this.chart.filters.count,
        groupby: this.chart.filters.metadata
      }, 'tendencies')
      this.searchExecuted = true
    },
    async download () {
      const response = await this.getXLSX({
        q: this.parseSearchData(this.filters),
        data: {
          return: this.chart.filters.count,
          groupby: this.chart.filters.metadata
        },
        frags: this.mapFragrance()
      })
      const url = window.URL.createObjectURL(new Blob([response]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileNames.analyticsTendencies)
      document.body.appendChild(link)
      link.click()
    },
    changeSort (newKey) {
      if (this.sortBy.key === newKey) {
        const test = !this.sortBy.desc
        this.$set(this.sortBy, 'desc', test)
      } else {
        this.$set(this.sortBy, 'key', newKey)
        this.$set(this.sortBy, 'desc', true)
      }
    },
    mapFragrance () {
      return this.fragrances.map(el => {
        return {
          'id': el.id,
          'c_mi_number': el.c_mi_number,
          'prod': el.prod
        }
      })
    }
  },
  watch: {
    ready: {
      deep: true,
      handler (value) {
        if (!_.isEqual(value, this.localReady)) {
          this.localReady = _.cloneDeep(value)
        }
      }
    },
    localReady: {
      deep: true,
      handler (value) {
        if (!_.isEqual(value, this.ready)) {
          this.$emit('update:ready', _.cloneDeep(value))
        }
      }
    },
    'chart.filters.metadataType': {
      handler (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.chart.filters.metadata = ''
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "../../../assets/scss/variables.scss";

.table-container {
  overflow: scroll;
  position: relative;
  max-height: calc(55vh - 340px);
  min-height: 205px;
}

.main-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 50px;
  width: 100%;
  cursor: pointer;
}

.additional-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  overflow: hidden;
  width: 98%;
  cursor: pointer;
}

.data {
  border-bottom: 1px solid #d1d4d7;
  transition: 0.1s background-color linear;
}

.data:hover {
  background-color: #f6f6f6;
}

.additional-data-hide {
  height: 0;
}

.additional-data-show {
  height: 50px;
  border-top: 1px dashed #d1d4d7
}

.scroller {
  height: 100%;
}

.column-header {
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
  transition: 0.1s background-color linear;
}

.column-header:hover {
  background-color: #f6f6f6;
}

.column-body {
  display: flex;
  justify-content: center;
  align-items: center;
}

.no-data {
  max-height: calc(55vh - 4rem);
  min-height: 523px;
  font-size: 18px;
}

.table-header {
  z-index: 3;
  position: sticky;
  top: 0;
  background-color: white;
  border: 1px solid #d1d4d7;
}

.no-hover:hover {
  background-color: unset;
}

.chart-filters-tab {
  overflow: hidden;
  margin-top: -1rem;
  z-index: 5;
  position: absolute;
  right: 0;
  height: 100%;
  width: 0;
  background-color: white;
  border-left: 0;
  transition: width 0.2s ease-in, border-left 0.2s ease-in;
}

.chart-filters-tab.show {
  width: 280px;
  border-left: 1px solid #d1d4d7;
}

.chart-filters-overlay {
  z-index: 4;
  width: 100%;
  height: 100%;
  position: absolute;
  margin-top: -1rem;
  margin-left: -1rem;
  background-color: rgba(0, 0, 0, 0);
  transition: background-color 0.2s ease-in;
}

.chart-filters-overlay.show {
  background-color: rgba(0, 0, 0, 0.1);
}

.chart-filters-overlay.hide {
  display: none;
}

.chart-filters-button {
  cursor: pointer;
  border-left: 1px solid #d1d4d7;
  border-bottom: 1px solid #d1d4d7;
  border-top: 1px solid #d1d4d7;
  z-index: 5;
  position: absolute;
  background-color: white;
  right: 0;
  width: 40px;
  height: 40px;
  bottom: 15px;
  transition: right 0.2s ease-in;
}

.chart-filters-button.show {
  right: 280px;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
}

.input-wrapper input {
  width: 100%;
}

.input-wrapper .el-select {
  width: 100%;
}

.input-wrapper label {
  color: #202124;
  margin-bottom: 2px;
  margin-left: 2px;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
}

.chart-filters-title {
  font-size: 1.1rem;
  white-space: nowrap;
}

.search-button {
  overflow: hidden;
  white-space: nowrap;
  border-radius: 5px;
}

.search-button {
  width: 230px;
  height: 48px;
  background-color: #300296;
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  font-weight: 800;
  line-height: 30px;
  letter-spacing: 0;
  border-radius: 4px;
  color: white;
  text-transform: uppercase;
  transition: background-color 0.2s linear;

  &:hover {
    color: white;
    background-color: #9256D9;
  }
}

.download-button {
  height: 48px;
  background-color: $celery-500;
  border-radius: 4px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 900;
  font-size: 16px;
  line-height: 30px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: white;

  transition: background-color 0.2s linear;

  &:hover {
    color: white;
    background-color: $celery-700;
  }
}
</style>
